import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTokenFromLink } from '../hooks/useTokenFromLink';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const inputStyle = {
  width: '100%',
  height: 48,
  padding: '0 40px 0 14px',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  fontSize: 14,
  color: '#111827',
  backgroundColor: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

/**
 * /reset-password — public page reached from the borrower password-reset
 * email. Reads the token from the link, strips it from the URL, posts to /api/auth/reset-password,
 * redirects to /login on success.
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  // Capture once, then strip the token from the address bar (history hygiene —
  // it's already POSTed in the body, so there's no backend leak). Refresh
  // afterward hits the "missing token" message below.
  // 🔑 hooks/useTokenFromLink: read once, stripped from the address bar, no-referrer while the page is open.
  const token = useTokenFromLink();
  const [pw, setPw]       = useState('');
  const [pwc, setPwc]     = useState('');
  const [showPw, setShowPw]   = useState(false);
  const [showPwc, setShowPwc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]   = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-black mb-2">Invalid reset link</h1>
          <p className="text-gray-500 text-sm mb-5">This link is missing its token. Please request a new one from the login page.</p>
          <Link to="/login" className="inline-block px-5 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ backgroundColor: '#3a6ad9' }}>
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8 || !/\d/.test(pw) || !/[A-Z]/.test(pw)) {
      setError('Password must be at least 8 characters and include a number and an uppercase letter.');
      return;
    }
    if (pw !== pwc) { setError('Passwords do not match.'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        newPassword: pw,
        confirmPassword: pwc,
      });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setError(err.response?.data?.error || 'This reset link has expired or is invalid. Please request a new one.');
      } else {
        setError(err.response?.data?.error || 'Could not reset your password. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-bold mb-2" style={{ color: '#15803d' }}>✓ Password updated</h1>
          <p className="text-gray-500 text-sm">Redirecting you to login…</p>
        </div>
      </div>
    );
  }

  const eyeBtn = {
    position: 'absolute', right: 10, top: 0, height: 48,
    background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 12, fontWeight: 600,
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/login" className="inline-block">
            <img src="/doorly_logo_black.png" alt="Doorly" style={{ height: 36, width: 'auto', display: 'inline-block' }} />
          </Link>
          <h1 className="text-2xl font-bold text-black mt-6 mb-1">Set a new password</h1>
          <p className="text-gray-500 text-sm">Enter and confirm your new password below.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          {error && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}
          <form onSubmit={submit} noValidate>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showPw ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)}
                  autoComplete="new-password" placeholder="New password" style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = '#3a6ad9')}
                  onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')} />
                <button type="button" onClick={() => setShowPw((v) => !v)} style={eyeBtn}>
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showPwc ? 'text' : 'password'} value={pwc} onChange={(e) => setPwc(e.target.value)}
                  autoComplete="new-password" placeholder="Confirm new password" style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = '#3a6ad9')}
                  onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')} />
                <button type="button" onClick={() => setShowPwc((v) => !v)} style={eyeBtn}>
                  {showPwc ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <p className="text-xs text-gray-400 mb-5">At least 8 characters, 1 uppercase letter, 1 number.</p>

            <button type="submit" disabled={submitting}
              className="btn-primary btn-full text-white hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: '#3a6ad9' }}>
              {submitting ? 'Updating…' : 'Set New Password'}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          <Link to="/login" className="font-semibold" style={{ color: '#3a6ad9' }}>
            Back to login
          </Link>
        </p>
      </div>
    </div>
  );
}
